import React, { useState } from 'react';
import { Device } from '../../lib/types';
import Modal, { ModalTitle, ModalActions } from '../../components/Modal/Modal';
import Button from '../../components/Button/Button';
import styles from './Devices.module.scss';

interface DeviceKeptModalProps {
  open: boolean;
  /** Dismiss the flow entirely (X / backdrop / Escape). */
  onClose: () => void;
  /** Return to the previous step — the cancel-reason picker. */
  onBack: () => void;
  device: Device | null;
  /** Marks the transfer not recovered so it stays tracked in Stalled. */
  onConfirm: (device: Device) => Promise<void>;
}

/**
 * Intermediate confirmation shown after the rescue picks "Adopter kept the
 * device and I don't have it" in the cancel-reason modal. It spells out what
 * confirming does (the transfer moves to Stalled for follow-up and never counts
 * toward the transfer rate) before the transfer is actually marked not recovered.
 */
export default function DeviceKeptModal({ open, onClose, onBack, device, onConfirm }: DeviceKeptModalProps) {
  const [submitting, setSubmitting] = useState(false);

  if (!device) {
    return null;
  }

  const currentDevice = device;

  async function handleConfirm() {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    try {
      // Await the mutation so a failure keeps the modal open for retry — the
      // error toast is raised by the handler in Devices, which re-throws.
      await onConfirm(currentDevice);
      onClose();
    } catch {
      // Kept open on failure; toast already surfaced the error.
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open={open} onClose={submitting ? () => {} : onClose}>
      <ModalTitle>Track recovery in Stalled</ModalTitle>
      <p className={styles.modalDescription}>
        You picked <b>&ldquo;Adopter kept the device and I don&rsquo;t have it.&rdquo;</b> Here&rsquo;s what happens
        when you confirm:
      </p>
      <div className={`${styles.outcomeBox} ${styles.outcomeBoxWarn}`}>
        <div className={styles.outcomeBoxIconWarn}>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
        </div>
        <div>
          <div className={styles.outcomeBoxLabel}>What happens</div>
          <div className={styles.outcomeBoxText}>
            This transfer moves to <b>Stalled Transfers</b> so you can follow up to recover device {device.id}. It never
            counts toward your transfer rate.
          </div>
        </div>
      </div>
      <ModalActions>
        <Button variant="ghost" onClick={onBack} disabled={submitting}>
          Back
        </Button>
        <Button variant="green" onClick={handleConfirm} disabled={submitting}>
          {submitting ? 'Working…' : 'Confirm — move to Stalled'}
        </Button>
      </ModalActions>
    </Modal>
  );
}
